import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Save,
  Image as ImageIcon,
  Tag,
  FileText
} from 'lucide-react';
import Button from '../../ui/Button';
import Input from '../../ui/Input';

interface SaveTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description: string; category: string }) => void;
}

const SaveTemplateModal: React.FC<SaveTemplateModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('custom');
  const [isSaving, setIsSaving] = useState(false);

  const categories = [
    { id: 'custom', label: t('pageBuilder.saveTemplate.categories.custom') },
    { id: 'saas', label: t('pageBuilder.saveTemplate.categories.saas') },
    { id: 'ecommerce', label: t('pageBuilder.saveTemplate.categories.ecommerce') },
    { id: 'lead', label: t('pageBuilder.saveTemplate.categories.lead') },
    { id: 'event', label: t('pageBuilder.saveTemplate.categories.event') },
    { id: 'other', label: t('pageBuilder.saveTemplate.categories.other') }
  ];

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) return;

    setIsSaving(true);
    try {
      await onSave({ name, description, category });
      setName('');
      setDescription('');
      setCategory('custom');
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Save className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('pageBuilder.saveTemplate.title')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('pageBuilder.saveTemplate.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FileText className="w-4 h-4" />
              {t('pageBuilder.saveTemplate.name')} *
            </label>
            <Input
              placeholder={t('pageBuilder.saveTemplate.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <ImageIcon className="w-4 h-4" />
              {t('pageBuilder.saveTemplate.description')}
            </label>
            <textarea
              placeholder={t('pageBuilder.saveTemplate.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Category */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Tag className="w-4 h-4" />
              {t('pageBuilder.saveTemplate.category')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    category === cat.id
                      ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-2 border-indigo-500'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-2 border-transparent hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || isSaving}
            leftIcon={<Save className="w-4 h-4" />}
            isLoading={isSaving}
          >
            {t('pageBuilder.saveTemplate.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SaveTemplateModal;
